import BillingTransactionsService from 'js/services/api/billing/transactions.js'
import Mixin from '../_transactions_mixin'

import template from './_transaction.pug'

const TYPE_CHARGE = 5
const TYPE_REFUND = 7
const TYPE_VALIDATION = 8
const TYPE_PARTIAL_VALIDATION = 12

const HOP_TYPE_ON = 1
const HOP_TYPE_OFF = 0

const PENALTY_STATUS_APPLIED = 1

// @vue/component
export default {
  template: template(),
  mixins: [Mixin],
  props: {
    transaction: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      penalizedTransactions: [],
      readyPenalty: false
    }
  },
  computed: {
    /*
     * Datos básicos de la transacción
     */

    // Fecha de la transacción
    date() {
      return this.timeFormat(this.transaction.operation_date)
    },

    // Tipo de la transacción
    type() {
      if (this.isValidation) {
        return this.$t('balance.transaction.type.validation')
      }
      if (this.isPartialValidation && this.isHopOn) {
        return this.$t('balance.transaction.type.hopOn')
      }
      if (this.isPartialValidation && this.isHopOff) {
        if (!this.chargedTotal) {
          return this.$t('balance.transaction.type.pendingHopOff')
        }
        return this.$t('balance.transaction.type.hopOff')
      }
      if (this.isCharge) {
        return this.$t('balance.transaction.type.recharge')
      }
      if (this.isRefund) {
        return this.$t('balance.transaction.type.refund')
      }
      return undefined
    },

    // Icono del tipo de la transacción
    typeIcon() {
      if (this.isValidation) {
        return 'ns-icon-ticket fs-iota text-gray2'
      }
      if (this.isPartialValidation && this.isHopOn) {
        return 'ns-icon-wayout'
      }
      if (this.isPartialValidation && this.isHopOff) {
        return 'ns-icon-wayback'
      }
      if (this.isCharge || this.isRefund) {
        return 'ns-icon-coins text-primary'
      }
      return undefined
    },

    // Color del tipo de la transacción
    typeColor() {
      if (this.isCharge || this.isRefund) {
        return 'text-primary'
      }
      return 'text-gray2'
    },

    // Comprueba si la transacción es una carga o no
    isCharge() {
      return this.transaction.type === TYPE_CHARGE
    },

    // Comprueba si la transacción es una carga de devolución o no
    isRefund() {
      return this.transaction.type === TYPE_REFUND
    },

    // Comprueba si la transacción es de subida o bajada
    isPartialValidation() {
      return this.transaction.type === TYPE_PARTIAL_VALIDATION
    },

    // Comprueba si al transacción es una validación
    isValidation() {
      return this.transaction.type === TYPE_VALIDATION
    },

    // Comprueba si al transacción es una validación anulada
    isVoidedValidation() {
      return this.isValidation && this.transaction.voided_validation
    },

    // Comprueba si la transacción es de subida
    isHopOn() {
      return this.isPartialValidation && this.transaction.hop_type === HOP_TYPE_ON
    },

    // Comprueba si la transacción es de bajada
    isHopOff() {
      return this.isPartialValidation && this.transaction.hop_type === HOP_TYPE_OFF
    },

    /*
     * Datos de la ruta
     */

    // Ruta de la transacción
    route() {
      return this.transaction.route || {}
    },

    // Nombre de la ruta de la transacción
    routeShortName() {
      return this.route.short_name
    },

    // Color de la ruta de la transacción
    routeColor() {
      return `background-color:${this.route.color};`
    },

    /*
     * Datos de la parada
     */

    hasDestination() {
      return this.transaction.has_destination
    },

    stopName() {
      if (this.hasDestination) { return this.multipleStopText }
      return this.oneStopText
    },

    stopFromName() {
      if (this.transaction.stop) {
        return this.transaction.stop.name
      }
      return ''
    },

    stopToName() {
      return this.transaction.stop_to ? this.transaction.stop_to.name : ''
    },

    multipleStopText() {
      const data = {stopFromName: this.stopFromName, stopToName: this.stopToName}
      if (Object.values(data).join().trim() === ',') return ''
      return this.$t('balance.stops.originDestination', data)
    },

    oneStopText() {
      const stopName = this.transaction.stop ? this.transaction.stop.name : ''
      return this.$t('balance.stops.onlyOrigin', {stopName})
    },

    /*
     * Importes
     */

    // Cambio de saldo que se produce en la transacción
    chargedTotal() {
      if (this.isVoidedValidation) {
        return 0
      }
      const transaction = this.transaction
      if (this.isRefund) {
        return transaction.amount || 0
      }
      if (this.isValidation || this.isPartialValidation) {
        return transaction.charged_total || 0
      }
      if (this.isCharge) {
        return transaction.recharge_amount || 0
      }
      return undefined
    },

    // Saldo bonificado al usuario
    bonusAmount() {
      const transaction = this.transaction
      if (this.isCharge) {
        return this.chargedTotal - (transaction.paid_amount || 0)
      }
      return undefined
    },

    // Si se pone símbolo positivo o negativo al cambio de saldo de la transacción
    chargedTotalSymbol() {
      if (this.isValidation || (this.isPartialValidation && this.chargedTotal)) {
        return '-'
      }
      return ''
    },

    // Saldo final que queda después de haber aplicado la transacción
    balanceAfter() {
      return this.transaction.balance_after || 0
    },

    /*
     * Coste del viaje
     */

    travelAmount() {
      const transaction = this.transaction
      if (this.isValidation || this.isPartialValidation) {
        return transaction.amount || 0
      }
      return undefined
    },

    /*
     * Viajes pendientes que se cobran
     */

    chargedTravels() {
      if (this.isValidation || this.isPartialValidation) {
        return this.transaction.charged_travels || 0
      }
      return undefined
    },

    /*
     * Viajes que quedan pendientes
     */

    pendingTravels() {
      if (this.isPartialValidation) {
        return this.transaction.pending_travels || 0
      }
      return undefined
    },

    /*
     * Penalizaciones
     */

    // Número de penalizaciones a realizar
    penalizations() {
      if (this.isPartialValidation) {
        return this.transaction.prev_passenger_count || 1
      }
      return undefined
    },

    // Cantidad todal a penalizar
    chargedPenalty() {
      if (this.isPartialValidation) {
        return this.transaction.charged_penalty || 0
      }
      return undefined
    },

    // Estado de la penalización
    penaltyStatus() {
      if (this.isPartialValidation) {
        return this.transaction.penalty_status
      }
      return undefined
    },

    // Cantidad de penalización unitaria de la transacción
    // Es igual a toda la penalización aplicada entre el número de pasajeros
    penalizationAmount() {
      if (this.isPartialValidation) {
        return this.chargedPenalty / this.penalizations
      }
      return undefined
    },

    showExtraInformation() {
      return this.showTravelAmount || this.showPendingAmount || this.showPendingTravel ||
        this.showPenalizations || this.showChargedTravel
    },

    // Muestra el importe del viaje si:
    // Es una validación de tipo 8
    // Es una validación parcial con coste del viaje y no queda pendiente
    showTravelAmount() {
      return this.travelAmount && !this.pendingTravels
    },

    // Muestra el importe pendiente de cobro si:
    // Es una validación de tipo 8
    // charged_total es 0
    showPendingAmount() {
      return this.isValidation && this.transaction.charged_total === 0
    },

    // Muestra el importe de un viaje que no se puede cobrar si
    // Es una validación parcial con coste del viaje pendiente
    showPendingTravel() {
      return this.pendingTravels
    },

    // Muestra penalizaciones si:
    // Están cargadas las penalizaciones y existe cantidad a penalizar
    showPenalizations() {
      return this.chargedPenalty && this.readyPenalty
    },

    // Muestra importe de viajes anteriores cobrados si:
    // Existe cantidad cobrada de un viaje anterior
    showChargedTravel() {
      return this.chargedTravels
    }
  },
  created() {
    this.getPenalizedTransactions()
  },
  methods: {
    getPenalizedTransactions() {
      if (this.chargedPenalty) {
        if (this.penaltyStatus === PENALTY_STATUS_APPLIED) {
          const params = {
            penalty_charged_transaction: this.transaction.operation_id,
            order_by: 'fetr_counter',
            order_dir: 'desc'
          }

          BillingTransactionsService.index(params).then(data => {
            this.penalizedTransactions = data
            this.readyPenalty = true
          })
        } else {
          this.readyPenalty = true
        }
      }
    },

    getPenaltyTransaction(n) {
      return this.penalizedTransactions[n]
    },

    getPenaltyTransactionOperationDate(n) {
      const transaction = this.penalizedTransactions[n]

      const TZ = this.administrativeArea.time_zone

      if (TZ) {
        const date = moment(transaction.operation_date).tz(TZ)
        return date.format('DD MMM HH:mm').replace('.', '')
      }
      return undefined
    },

    timeFormat(time){
      let date =new Date(time)
      let newDate = date.toLocaleDateString('en-es', { day:"numeric", month:"short"})
      let hour = date.getHours()
      let minutes = date.getMinutes()
      if (parseInt(minutes)<10){
        minutes = `0${minutes}`
      }
      return `${newDate} ${hour}:${minutes}`
    }
  }
}
